"use client";

import React, { useEffect, useRef, useState } from "react";
import { Activity, Cpu, ShieldCheck } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const trustBadges = [{
  title: "Certified Expertise",
  description: "Our inspectors are InterNACHI certified and Florida licensed, ensuring every evaluation meets the highest professional standards for accuracy and thoroughness.",
  icon: ShieldCheck
}, {
  title: "Advanced Technology",
  description: "We utilize thermal imaging and high-resolution sewer cameras to detect hidden issues that standard visual inspections often miss, protecting your investment.",
  icon: Cpu
}, {
  title: "Rapid Reporting",
  description: "Receive a comprehensive, easy-to-read digital report within 24 hours of your inspection, complete with detailed photos and prioritized repair recommendations.",
  icon: Activity
}];
export default function Kwaliteitsgarantie() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="224807" id="benefits" className="bg-background text-foreground py-20 px-4 md:px-8 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-64 bg-primary/5 blur-[120px] pointer-events-none rounded-full" />

      <div ref={ref} className={`max-w-7xl mx-auto relative z-10 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="text-center mb-16">
          <h2 className="font-default text-3xl md:text-4xl font-bold text-secondary mb-4 tracking-tight">
            Why Choose Our Inspections
          </h2>
          <p className="font-default text-muted-foreground max-w-2xl mx-auto text-lg">
            Providing South Florida homeowners with the clarity and confidence 
            needed to make informed real estate decisions through precision diagnostics.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {trustBadges.map((badge, i) => {
          const Icon = badge.icon;
          return <div data-index={i} key={i} className="h-full">
                <Card className="h-full bg-card border-border shadow-sm hover:shadow-md transition-all duration-300 group rounded-lg">
                  <CardHeader className="pb-4">
                    <div className="size-14 rounded-lg bg-accent flex items-center justify-center mb-4 group-hover:bg-primary/10 transition-colors duration-300">
                      <Icon className="size-7 text-primary" strokeWidth={1.5} />
                    </div>
                    <CardTitle className="font-default text-xl text-foreground">
                      {badge.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="font-default text-base leading-relaxed text-muted-foreground">
                      {badge.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}